import {
  allowsImpersonation,
  canPerform,
  describeRolesAllowedTo,
  isUserRole,
  type PolicyOperation,
} from "@headerhawk/core/roles";
import { isDemoTab, isImpersonationTab } from "@/lib/auth/impersonation";
import type { AuthMeResponse, UserRole } from "@/lib/api/client";
import { useProvisioning } from "./use-provisioning";

export interface CurrentRole {
  role: UserRole;
  /** False until provisioning has answered with the user's role. */
  isResolved: boolean;
  /**
   * Whether the current role may call `operation`, per the API's own
   * `ROLE_POLICY` — and, in an operator's read-only view of a member
   * (task 394), its `IMPERSONATION_POLICY`. Ask about the operation a control
   * performs rather than naming a role, so the dashboard cannot drift from
   * what the API enforces.
   */
  can: (operation: PolicyOperation) => boolean;
  /**
   * Why a control performing `operation` is disabled, for its tooltip
   * (`DisabledReason`, task 450), or null when it is not, or while the role
   * is still unknown. `action` finishes the sentence: "change rules" reads
   * "Only owners and admins can change rules."
   */
  whyNot: (operation: PolicyOperation, action: string) => string | null;
}

/**
 * The role a profile carries, or `viewer` when there is none yet.
 *
 * `viewer` is the safe default: until the profile loads, a control briefly
 * hides rather than being offered to someone it will refuse.
 */
export function roleFromProfile(profile: AuthMeResponse | null): UserRole {
  const role = profile?.user.role;
  return isUserRole(role) ? role : "viewer";
}

/**
 * The signed-in user's role in their tenant.
 *
 * Read from provisioning's `POST /auth/me`, which `RequireAuth` has already
 * made on every dashboard page, so this costs no request of its own.
 *
 * Hiding a control is presentation, not enforcement: the API's role policy is
 * what refuses the request, and a page can outlive a demotion, so every
 * mutation still has to show a 403 legibly (see `describeRequestError`).
 */
export function useCurrentRole(): CurrentRole {
  const { profile } = useProvisioning();
  const role = roleFromProfile(profile);
  // An operator's read-only view (task 394): the member's role still bounds
  // what is shown, and the API's `IMPERSONATION_POLICY` removes every write.
  const readOnly = isImpersonationTab();
  const isResolved = isUserRole(profile?.user.role);
  const can = (operation: PolicyOperation) =>
    canPerform(role, operation) &&
    (!readOnly || allowsImpersonation(operation));
  return {
    role,
    isResolved,
    can,
    whyNot: (operation, action) => {
      if (can(operation)) return null;
      // The demo's member and an operator's view are read-only whatever the
      // role, so naming the roles that may would answer the wrong question.
      if (isDemoTab()) return "The demo is read-only.";
      if (readOnly) return "This view is read-only.";
      if (!isResolved) return null;
      return `Only ${describeRolesAllowedTo(operation)} can ${action}.`;
    },
  };
}
